/**
 * Breadcrumb and settings-tab constants.
 *
 * The nav item list that used to live here moved to
 * `components/sidebar/data/navItems.tsx` when the floating rail was replaced by
 * the v2 sidebar — nav data belongs with the sidebar that renders it.
 *
 * @module Navigation
 */

import homeIcon from "@/assets/navbar/home.svg"

/**
 * Returns the breadcrumb root icon and its link target.
 * The first breadcrumb leaf always represents the parent route the user navigated from.
 * Since all pages descend from Home (/ui), this always returns the home icon linking to /ui.
 */
export const getBreadcrumbRoot = (): { icon: string; href: string } => ({
  icon: homeIcon,
  href: '/ui',
});

/**
 * Settings page tab identifiers.
 */
export const SETTINGS_TABS = {
  PROFILE: 'profile',
  CONNECTIONS: 'connections',
  DEVELOPER_CONNECTIONS: 'developer-connections',
  API_SETTINGS: 'api-settings',
  BRAND_DETAILS: 'brand-details',
  ORGANIZATION: 'organization',
} as const;

export type SettingsTab = typeof SETTINGS_TABS[keyof typeof SETTINGS_TABS];

/**
 * Breadcrumb label derived from the URL. Used as the fallback for routes the
 * sidebar's nav tree does not model, such as /ui/settings/profile.
 */
export const getBreadcrumbData = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 1) {
    return {
      isHomePage: true,
      currentPageLabel: 'Home'
    };
  }

  const currentSegment = segments[segments.length - 1];
  // Sentence case, not title case: capitalise the first word and keep the rest
  // lower, except known acronyms which stay upper ("api-settings" → "API
  // settings", not "Api Settings").
  const ACRONYMS = new Set(['api', 'ai', 'seo', 'aeo', 'url', 'id']);
  const formattedLabel = currentSegment
    .split('-')
    .map((word, index) => {
      if (ACRONYMS.has(word)) return word.toUpperCase();
      return index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    })
    .join(' ');

  return {
    isHomePage: false,
    currentPageLabel: formattedLabel
  };
};
